/**
 * Bounded, paced worker pool for the cashout pollers (MyBets, IllinoisMyBets).
 *
 * Runs `task(item)` over `items` with:
 *  - at most `concurrency` tasks in flight;
 *  - staggered start: worker N waits N × `staggerMs` before its first task,
 *    so a poll never fires all its requests in the same instant;
 *  - a pause of `gapMs` between two tasks of the same worker;
 *  - cancellation: `isCancelled()` is checked before every task and after
 *    every pause — a destroyed panel stops pulling work.
 *
 * Every task is awaited and its error contained (`onError`), so one failure
 * cannot strand the pool, and the returned promise always resolves.
 */

/**
 * Cancellable sleeps. `cancelAll()` clears every pending timer and resolves
 * its promise at once, so nothing is left scheduled after a component is
 * destroyed (no timer outliving the instance, no dangling closure).
 */
export function createSleeper() {
    const pending = new Map(); // timeoutId -> resolve
    return {
        sleep(ms) {
            if (!(ms > 0)) return Promise.resolve();
            return new Promise(resolve => {
                const id = setTimeout(() => {
                    pending.delete(id);
                    resolve();
                }, ms);
                pending.set(id, resolve);
            });
        },
        cancelAll() {
            for (const [id, resolve] of pending) {
                clearTimeout(id);
                resolve();
            }
            pending.clear();
        },
        get pendingCount() {
            return pending.size;
        },
    };
}

/**
 * @param {Array} items
 * @param {(item) => Promise} task
 * @param {Object} opts
 * @param {number} opts.concurrency   max tasks in flight
 * @param {number} [opts.staggerMs=0] delay between the start of two workers
 * @param {number} [opts.gapMs=0]     pause between two tasks of the same worker
 * @param {() => boolean} [opts.isCancelled]
 * @param {(ms) => Promise} [opts.sleep] cancellable sleep (createSleeper().sleep)
 * @param {(item, error) => void} [opts.onError]
 */
export async function runWorkerPool(items, task, {
    concurrency,
    staggerMs = 0,
    gapMs = 0,
    isCancelled = () => false,
    sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms)),
    onError = () => {},
} = {}) {
    if (!items || items.length === 0) return;

    let cursor = 0;
    const workerCount = Math.min(Math.max(1, concurrency || 1), items.length);

    const worker = async (slot) => {
        if (slot > 0 && staggerMs > 0) {
            await sleep(slot * staggerMs);
        }
        for (;;) {
            if (isCancelled()) return;
            const index = cursor++;               // single-threaded: no race here
            if (index >= items.length) return;
            try {
                await task(items[index]);
            } catch (e) {
                onError(items[index], e);
            }
            if (gapMs > 0 && cursor < items.length) {
                await sleep(gapMs);
            }
        }
    };

    const workers = [];
    for (let i = 0; i < workerCount; i++) workers.push(worker(i));
    await Promise.all(workers);
}
